import React, { useState, useEffect, useRef } from "react";
import { Card, Typography, Button, Box, OutlinedInput, IconButton } from "@mui/material";
import NotificationsIcon from '@mui/icons-material/Notifications';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import MinimizeIcon from '@mui/icons-material/Minimize';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Config from '../../../config';
import { formatLocalDateTime } from '../../../utils/dateTime';
import { useLatestRef } from '../../../utils/hooks';

const PAGE_SIZE = 5;

const categoryStyle = (category) => ({
  AI:       { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.3)' },
  MOTION:   { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)'  },
  SYSTEM:   { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.3)'  },
  ERROR:    { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.3)'   },
}[String(category).toUpperCase()] || { color: '#64748b', bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.3)' });

export default function AlertPanel({ robot, apiConfig }) {
  const [position, setPosition]       = useState({ x: 520, y: 20 });
  const [isDragging, setIsDragging]   = useState(false);
  const [dragStart, setDragStart]     = useState({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasDragged, setHasDragged]   = useState(false);
  const [alerts, setAlerts]           = useState([]);
  const [alertFilter, setAlertFilter] = useState("");
  const [expandedImageId, setExpandedImageId] = useState(null);
  const [page, setPage]               = useState(1);
  const [totalCount, setTotalCount]   = useState(0);
  const [toastAlert, setToastAlert]   = useState(null);
  const wsRef       = useRef(null);
  const toastTimer  = useRef(null);

  const totalPages   = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const unreadCount  = alerts.filter(a => a.Status === 'NEW').length;

  const playAlertSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();

      // Compressor to prevent clipping at high gain
      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.value = -6;
      compressor.ratio.value = 4;
      compressor.connect(ctx.destination);

      const tone = (t, freq, dur, type = 'square', vol = 1.0) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(compressor);
        osc.frequency.value = freq;
        osc.type = type;
        gain.gain.setValueAtTime(vol, t);
        gain.gain.setValueAtTime(vol, t + dur - 0.03);
        gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
        osc.start(t);
        osc.stop(t + dur);
      };

      const t = ctx.currentTime;
      // Three loud rising chirps followed by a long blast
      tone(t,        1200, 0.12, 'square', 1.0);
      tone(t + 0.15, 1000, 0.12, 'square', 1.0);
      tone(t + 0.30, 1400, 0.18, 'square', 1.0);
      tone(t + 0.55, 1200, 0.12, 'square', 1.0);
      tone(t + 0.70, 1000, 0.12, 'square', 1.0);
      tone(t + 0.85, 1400, 0.18, 'square', 1.0);
      // Final sustained alarm tone
      tone(t + 1.10, 1320, 0.60, 'sawtooth', 0.9);
    } catch (e) {
      console.warn('AlertPanel audio error', e);
    }
  };

  const showToast = (alert) => {
    clearTimeout(toastTimer.current);
    setToastAlert(alert);
    toastTimer.current = setTimeout(() => setToastAlert(null), 25000);
  };

  // ── Drag ──────────────────────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setHasDragged(false);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  const handleMouseMove = (e) => {
    if (isDragging) {
      setHasDragged(true);
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseMoveRef = useLatestRef(handleMouseMove);
  const handleMouseUpRef = useLatestRef(handleMouseUp);
  const apiConfigRef = useLatestRef(apiConfig);

  useEffect(() => {
    if (isDragging) {
      const onMouseMove = (event) => handleMouseMoveRef.current(event);
      const onMouseUp = () => handleMouseUpRef.current();
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      return () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
    }
  }, [isDragging, handleMouseMoveRef, handleMouseUpRef]);

  // ── Fetch alerts ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!robot) return;
    apiConfigRef.current.fetchAlertsPageByRobotId(robot.RobotId, page, PAGE_SIZE).then(res => {
      const items = res?.results?.RobotResponse || [];
      setAlerts(items);
      setTotalCount(res?.count ?? res?.results?.count ?? 0);
    }).catch(console.error);
  }, [robot, page, apiConfigRef]);

  // ── WebSocket ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!robot) return;
    const ws = new WebSocket(Config.robot_alert_ws);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.Reported_By === robot.RobotId) {
          if (page === 1) setAlerts(prev => [msg, ...prev.slice(0, PAGE_SIZE - 1)]);
          setTotalCount(c => c + 1);
          showToast(msg);
          playAlertSound();
        }
      } catch (e) {
        console.error('AlertPanel WS parse error', e);
      }
    };
    ws.onerror = (e) => console.error('AlertPanel WS error', e);
    return () => { ws.close(); wsRef.current = null; };
  }, [robot, page]);

  const handleOpen = () => {
    if (!hasDragged) setIsMinimized(false);
  };

  const handleUpdateStatus = (alert, newStatus) => {
    apiConfig.updateAlert(alert.Id, { ...alert, Status: newStatus })
      .then(() => setAlerts(prev => prev.map(a => a.Id === alert.Id ? { ...a, Status: newStatus } : a)))
      .catch(console.error);
  };

  const filtered = alerts.filter(a =>
    [a.Description, a.Category, a.Subcategory, a.Location, a.Site]
      .some(v => String(v || '').toLowerCase().includes(alertFilter.toLowerCase()))
  );

  const fmtTime = (iso) => {
    if (!iso) return '';
    return formatLocalDateTime(iso, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const imgSrc = (b64) => b64 ? `data:image/jpeg;base64,${b64}` : null;

  const toastStyle = toastAlert ? categoryStyle(toastAlert.Category) : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Prominent toast for incoming alerts ───────────────────────────── */}
      {toastAlert && toastStyle && (
        <Box
          sx={{
            position: 'fixed', top: 28, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9999, minWidth: 340, maxWidth: 480,
            bgcolor: 'rgba(15,23,42,0.97)',
            border: `2px solid ${toastStyle.color}`,
            borderRadius: 2,
            boxShadow: `0 0 0 4px ${toastStyle.bg}, 0 24px 48px rgba(0,0,0,0.7)`,
            overflow: 'hidden',
            animation: 'alertSlideIn 0.25s ease-out',
            '@keyframes alertSlideIn': {
              from: { opacity: 0, transform: 'translateX(-50%) translateY(-16px)' },
              to:   { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
            },
          }}
        >
          {/* Coloured top bar */}
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${toastStyle.color}, transparent)` }} />

          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <NotificationsIcon sx={{ color: toastStyle.color, fontSize: '1.6rem', flexShrink: 0, mt: 0.25 }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
                <Box sx={{ px: 0.75, py: 0.1, borderRadius: 0.5, bgcolor: toastStyle.bg, border: `1px solid ${toastStyle.border}` }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: toastStyle.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {toastAlert.Category || 'Alert'}
                  </Typography>
                </Box>
                {toastAlert.Subcategory && (
                  <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase' }}>
                    {toastAlert.Subcategory}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.62rem', color: '#475569', ml: 'auto' }}>
                  {fmtTime(toastAlert.Timestamp || toastAlert.CreatedAt)}
                </Typography>
              </Box>
              <Typography sx={{ color: '#f1f5f9', fontSize: '0.88rem', fontWeight: 600, lineHeight: 1.4 }}>
                {toastAlert.Description || '—'}
              </Typography>
              {(toastAlert.Location || toastAlert.Site) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                  <LocationOnIcon sx={{ fontSize: '0.7rem', color: '#475569' }} />
                  <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                    {[toastAlert.Site, toastAlert.Location, toastAlert.Floor].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              )}
            </Box>

            <IconButton
              size="small"
              onClick={() => setToastAlert(null)}
              sx={{ color: '#475569', p: 0.25, '&:hover': { color: 'white' }, flexShrink: 0 }}
            >
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>

          {/* Auto-dismiss progress bar */}
          <Box sx={{ height: 3, bgcolor: '#1e293b' }}>
            <Box sx={{
              height: '100%', bgcolor: toastStyle.color, transformOrigin: 'left',
              animation: 'toastProgress 6s linear forwards',
              '@keyframes toastProgress': { from: { transform: 'scaleX(1)' }, to: { transform: 'scaleX(0)' } },
            }} />
          </Box>
        </Box>
      )}

      {/* ── Draggable panel ───────────────────────────────────────────────── */}
      <Box sx={{ position: 'absolute', zIndex: 40, left: `${position.x}px`, top: `${position.y}px`, cursor: isDragging ? 'grabbing' : 'grab', ...(isMinimized ? {} : { width: '420px', maxHeight: 'calc(100vh - 150px)' }) }}>
        {isMinimized ? (
          <Box
            onMouseDown={handleMouseDown}
            onClick={handleOpen}
            sx={{
              width: 64, height: 64,
              background: unreadCount > 0
                ? 'linear-gradient(135deg, #ef4444, #b91c1c)'
                : 'linear-gradient(135deg, #f59e0b, #b45309)',
              borderRadius: '50%',
              boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'grab',
              '&:hover': { transform: 'scale(1.1)' },
              transition: 'transform 0.2s',
              position: 'relative',
            }}
          >
            <NotificationsIcon sx={{ color: 'white', fontSize: '2rem' }} />
            {unreadCount > 0 && (
              <Box sx={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#ef4444', lineHeight: 1 }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Card sx={{ bgcolor: 'rgba(15,23,42,0.98)', backdropFilter: 'blur(8px)', border: '1px solid #334155', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{ bgcolor: '#1e293b', borderBottom: '1px solid #334155', px: 2, py: 1.5, cursor: isDragging ? 'grabbing' : 'grab', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OpenWithIcon sx={{ color: '#64748b', fontSize: '1rem' }} />
            <NotificationsIcon sx={{ color: '#f59e0b', fontSize: '1rem' }} />
            <Typography variant="subtitle2" sx={{ color: 'white' }}>Alerts</Typography>
            {alerts.filter(a => a.Status === 'NEW').length > 0 && (
              <Box sx={{ px: 0.75, py: 0.1, borderRadius: 0.5, bgcolor: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)' }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#ef4444' }}>
                  {alerts.filter(a => a.Status === 'NEW').length} new
                </Typography>
              </Box>
            )}
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setIsMinimized(true)}
            sx={{ color: '#94a3b8', borderColor: '#475569', minWidth: '28px', height: '28px', p: 0, '&:hover': { color: 'white', borderColor: 'white' } }}
          >
            <MinimizeIcon sx={{ fontSize: '0.75rem' }} />
          </Button>
        </Box>

        {/* Filter */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
          <OutlinedInput
            fullWidth
            placeholder="Filter by description, category, location..."
            value={alertFilter}
            onChange={(e) => setAlertFilter(e.target.value)}
            startAdornment={<SearchIcon sx={{ color: '#64748b', fontSize: '1rem', ml: 0.5, mr: 0.5 }} />}
            sx={{ bgcolor: '#020617', color: 'white', height: '34px', fontSize: '0.8rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' } }}
          />
        </Box>

        {/* Alert list */}
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          {filtered.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotificationsIcon sx={{ color: '#334155', fontSize: '2rem', mb: 1 }} />
              <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>No alerts</Typography>
            </Box>
          ) : (
            filtered.map((alert, idx) => {
              const style      = categoryStyle(alert.Category);
              const isNew      = alert.Status === 'NEW';
              const isTrue     = alert.Status === 'TRUE';
              const isFalse    = alert.Status === 'FALSE';
              const isClosed   = alert.Status === 'CLOSED';
              const isRead     = isTrue || isFalse;
              const thumb      = imgSrc(alert.Image);
              const isExpanded = expandedImageId === alert.Id;

              return (
                <Box
                  key={alert.Id || idx}
                  sx={{
                    borderBottom: '1px solid #1e293b',
                    borderLeft: '3px solid',
                    borderLeftColor: isNew ? style.color : isTrue ? '#f59e0b' : isFalse ? '#475569' : 'transparent',
                    bgcolor: isNew ? style.bg : 'transparent',
                    opacity: isNew ? 1 : isRead ? 0.7 : 0.35,
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: 'rgba(30,41,59,0.5)', opacity: 1 },
                  }}
                >
                  {/* Main row */}
                  <Box sx={{ px: 1.5, py: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {/* Thumbnail */}
                    {thumb && (
                      <Box
                        component="img"
                        src={thumb}
                        onClick={() => setExpandedImageId(isExpanded ? null : alert.Id)}
                        sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 0.75, flexShrink: 0, cursor: 'pointer', border: '1px solid #334155', '&:hover': { borderColor: style.color } }}
                      />
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Badges row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4, flexWrap: 'wrap' }}>
                        {alert.Category && (
                          <Box sx={{ px: 0.6, py: 0.05, borderRadius: 0.5, bgcolor: style.bg, border: `1px solid ${style.border}` }}>
                            <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: style.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {alert.Category}
                            </Typography>
                          </Box>
                        )}
                        {alert.Subcategory && (
                          <Box sx={{ px: 0.6, py: 0.05, borderRadius: 0.5, bgcolor: 'rgba(30,41,59,0.8)', border: '1px solid #334155' }}>
                            <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {alert.Subcategory}
                            </Typography>
                          </Box>
                        )}
                        <Typography sx={{ color: '#475569', fontSize: '0.65rem', ml: 'auto' }}>
                          {fmtTime(alert.Timestamp || alert.CreatedAt)}
                        </Typography>
                      </Box>

                      {/* Description */}
                      <Typography sx={{ color: isNew ? '#e2e8f0' : '#64748b', fontSize: '0.78rem', lineHeight: 1.4, mb: 0.3 }}>
                        {alert.Description || '—'}
                      </Typography>

                      {/* Location */}
                      {(alert.Location || alert.Site) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <LocationOnIcon sx={{ fontSize: '0.7rem', color: '#475569' }} />
                          <Typography sx={{ color: '#475569', fontSize: '0.68rem' }}>
                            {[alert.Site, alert.Location, alert.Floor].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Status buttons */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                      {/* Set TRUE (real alert) */}
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateStatus(alert, 'TRUE')}
                        title="True alert"
                        disabled={isClosed}
                        sx={{
                          width: 26, height: 26, p: 0,
                          border: '1px solid',
                          borderColor: isTrue ? '#f59e0b' : '#334155',
                          color: isTrue ? '#f59e0b' : '#475569',
                          bgcolor: isTrue ? 'rgba(245,158,11,0.1)' : 'transparent',
                          borderRadius: 0.75,
                          '&:hover': { color: '#f59e0b', borderColor: '#f59e0b', bgcolor: 'rgba(245,158,11,0.15)' },
                          '&.Mui-disabled': { opacity: 0.2 },
                        }}
                      >
                        <WarningAmberIcon sx={{ fontSize: '0.75rem' }} />
                      </IconButton>
                      {/* Set FALSE (false alarm) */}
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateStatus(alert, 'FALSE')}
                        title="False alarm"
                        disabled={isClosed}
                        sx={{
                          width: 26, height: 26, p: 0,
                          border: '1px solid',
                          borderColor: isFalse ? '#64748b' : '#334155',
                          color: isFalse ? '#94a3b8' : '#475569',
                          bgcolor: isFalse ? 'rgba(100,116,139,0.15)' : 'transparent',
                          borderRadius: 0.75,
                          '&:hover': { color: '#94a3b8', borderColor: '#94a3b8', bgcolor: 'rgba(100,116,139,0.2)' },
                          '&.Mui-disabled': { opacity: 0.2 },
                        }}
                      >
                        <BlockIcon sx={{ fontSize: '0.75rem' }} />
                      </IconButton>
                      {/* Close alert — only available after categorisation */}
                      {isRead && (
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateStatus(alert, 'CLOSED')}
                          title="Close alert"
                          sx={{
                            width: 26, height: 26, p: 0,
                            border: '1px solid #334155',
                            color: '#475569',
                            borderRadius: 0.75,
                            '&:hover': { color: '#ef4444', borderColor: '#ef4444', bgcolor: 'rgba(239,68,68,0.1)' },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: '0.75rem' }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Expanded image */}
                  {isExpanded && thumb && (
                    <Box sx={{ px: 1.5, pb: 1.25 }}>
                      <Box
                        component="img"
                        src={thumb}
                        alt={alert.Description}
                        sx={{ width: '100%', borderRadius: 1, border: '1px solid #334155', display: 'block' }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Pagination */}
        <Box sx={{ borderTop: '1px solid #1e293b', px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography sx={{ color: '#475569', fontSize: '0.7rem' }}>
            {totalCount > 0 ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount}` : 'No results'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              sx={{ width: 26, height: 26, color: page === 1 ? '#334155' : '#94a3b8', '&:hover': { color: 'white' } }}
            >
              <ChevronLeftIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', minWidth: 60, textAlign: 'center' }}>
              {page} / {totalPages}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              sx={{ width: 26, height: 26, color: page === totalPages ? '#334155' : '#94a3b8', '&:hover': { color: 'white' } }}
            >
              <ChevronRightIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>
        </Box>
          </Card>
        )}
      </Box>
    </>
  );
}
